// src/components/StepNode.js

import React, { useState, useRef, useEffect } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Mail as MailIcon,
  Message as MessageIcon,
  Timer as TimerIcon,
  CallSplit as CallSplitIcon,
  Flag as FlagIcon,
  Http as HttpIcon,
  Stop as StopIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import ConnectionModal from './ConnectionModal';

const conditionTypes = [
  { value: 'always', label: 'Always' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'opened', label: 'Opened' },
  { value: 'replied', label: 'Replied' },
  { value: 'custom', label: 'Custom Condition' },
];

const triggerTypes = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'delay', label: 'Time Delay' },
  { value: 'funnel_change', label: 'Funnel Step Change' },
  { value: 'event', label: 'Event Based' },
  { value: 'condition', label: 'Condition Met' },
  { value: 'manual', label: 'Manual Trigger' },
];

const delayUnits = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];

const getStepIcon = (stepType) => {
  switch (stepType) {
    case 'email':
      return MailIcon;
    case 'sms':
      return MessageIcon;
    case 'voice':
      return PhoneIcon;
    case 'chat':
      return ChatIcon;
    case 'wait_step':
      return TimerIcon;
    case 'validation_step':
      return CallSplitIcon;
    case 'goal':
      return FlagIcon;
    case 'webhook':
      return HttpIcon;
    case 'end':
      return StopIcon;
    default:
      return MailIcon;
  }
};

const StepNode = ({
  step,
  isSelected,
  onClick,
  onDelete,
  onConnectTo,
  onEditConnection,
  onDeleteConnection,
  steps,
  connections,
  icon,
  isEntryPoint,
  isGoal,
  onStartConnection,
  onCompleteConnection,
  isConnecting,
  isConnectionSource,
  journey,
}) => {
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedTargetStep, setSelectedTargetStep] = useState('');
  const [triggerType, setTriggerType] = useState('immediate');
  const [delayDuration, setDelayDuration] = useState('');
  const [delayUnit, setDelayUnit] = useState('minutes');
  const [funnelStep, setFunnelStep] = useState('');
  const [eventType, setEventType] = useState('');
  const [conditionLabel, setConditionLabel] = useState('');
  const [conditionConfig, setConditionConfig] = useState({
    type: 'field_equals',
    field: '',
    value: ''
  });
  const [priority, setPriority] = useState(1);
  const [editingConnection, setEditingConnection] = useState(null);
  const [showConnectionHandles, setShowConnectionHandles] = useState(false);
  const nodeRef = useRef(null);
  const Icon = getStepIcon(step.step_type);
  const isPending = step.is_pending;

  // Log props when modal opens
  useEffect(() => {
    if (isConnectionModalOpen) {
      console.log('StepNode - Connection Modal Opened:', {
        stepId: step.id,
        stepName: step.name,
        journey,
        journeyFunnelId: journey?.funnel,
        funnelData: journey?.funnel_data
      });
    }
  }, [isConnectionModalOpen, step, journey]);

  // Helper function to validate connection data
  const isValidConnection = () => {
    switch (triggerType) {
      case 'delay':
        return delayDuration > 0 && delayUnit;
      case 'funnel_change':
        return funnelStep;
      case 'event':
        return eventType;
      case 'condition':
        return conditionLabel && conditionConfig.field && conditionConfig.value;
      default:
        return true;
    }
  };

  const handleConnect = (e, position) => {
    e.stopPropagation();
    console.log('StepNode - Starting connection:', {
      stepId: step.id,
      stepName: step.name,
      journey,
      journeyFunnelId: journey?.funnel,
      funnelData: journey?.funnel_data
    });
    
    if (nodeRef.current) {
      const rect = nodeRef.current.getBoundingClientRect();
      const nodePosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      onStartConnection(step.id, nodePosition);
      setIsConnectionModalOpen(true);
    }
  };

  const handleNodeClick = (e) => {
    e.stopPropagation();
    if (isConnecting && !isConnectionSource) {
      onCompleteConnection(step.id);
    } else {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    setShowConnectionHandles(true);
  };

  const handleMouseLeave = () => {
    setShowConnectionHandles(false);
  };

  const handleEditConnection = (connection, e) => {
    e.stopPropagation();
    console.log('StepNode - Editing Connection:', {
      connection,
      stepId: step.id,
      stepName: step.name
    });

    setEditingConnection(connection);
    setSelectedTargetStep(connection.to_step);
    setTriggerType(connection.trigger_type);
    setDelayDuration(connection.delay_duration || '');
    setDelayUnit(connection.delay_unit || 'minutes');
    setFunnelStep(connection.funnel_step || '');
    setEventType(connection.event_type || '');
    setConditionLabel(connection.condition_label || '');
    setConditionConfig(connection.condition_config || { type: 'field_equals', field: '', value: '' });
    setPriority(connection.priority || 1);
    setIsConnectionModalOpen(true);
  };

  const handleCreateConnection = (config) => {
    console.log('StepNode - Creating/Updating Connection:', {
      stepId: step.id,
      stepName: step.name,
      config,
      editingConnection
    });

    if (editingConnection) {
      onEditConnection(editingConnection.id, {
        ...config,
        from_step: step.id,
        to_step: parseInt(selectedTargetStep)
      });
    } else {
      onConnectTo(step.id, parseInt(selectedTargetStep), config);
    }

    // Reset form
    setSelectedTargetStep('');
    setTriggerType('immediate');
    setDelayDuration('');
    setDelayUnit('minutes');
    setFunnelStep('');
    setEventType('');
    setConditionLabel('');
    setConditionConfig({ type: 'field_equals', field: '', value: '' });
    setPriority(1);
    setEditingConnection(null);
    setIsConnectionModalOpen(false);
  };

  const handleDeleteConnection = (connectionId, e) => {
    e.stopPropagation();
    onDeleteConnection(connectionId);
  };

  const availableSteps = steps.filter(s => s.id !== step.id);
  const outgoingConnections = connections?.filter(c => c.from_step === step.id) || [];

  return (
    <>
      <Paper
        ref={nodeRef}
        elevation={isSelected ? 8 : 2}
        onClick={handleNodeClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: '100%',
          p: 2,
          cursor: 'pointer',
          border: 2,
          borderColor: isSelected ? 'primary.main' : 'transparent',
          opacity: isPending ? 0.7 : 1,
          transition: 'all 0.2s',
          mb: 2,
          position: 'relative',
          '&:hover': {
            elevation: 4,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isPending ? <CircularProgress size={20} /> : <Icon />}
          </Box>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {step.name}
          </Typography>
          <Tooltip title="Delete step">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(step.id);
              }}
              sx={{
                p: 0.5,
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="body2" color="textSecondary">
          {step.step_type}
        </Typography>

        {/* Connection Handles */}
        {!isGoal && (showConnectionHandles || isConnecting) && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: -12,
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Tooltip title="Connect to next step">
              <Box
                onClick={(e) => handleConnect(e, 'bottom')}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: isConnectionSource ? 'primary.main' : 'background.paper',
                  border: 2,
                  borderColor: isConnectionSource ? 'primary.main' : 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <AddIcon 
                  fontSize="small" 
                  sx={{ 
                    color: isConnectionSource ? 'white' : 'primary.main',
                    fontSize: 16,
                    transform: 'rotate(90deg)', // Rotate the plus icon to point downward
                  }} 
                />
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Existing Connections List */}
        {outgoingConnections.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Connections:
            </Typography>
            {outgoingConnections.map((connection) => {
              const targetStep = steps.find(s => s.id === connection.to_step);
              const isDeleting = connection.isDeleting;
              
              return (
                <Box
                  key={connection.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    opacity: isDeleting ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Typography 
                    variant="caption" 
                    noWrap 
                    sx={{ 
                      flex: 1,
                      color: isDeleting ? 'error.main' : 'text.secondary',
                    }}
                  >
                    → {targetStep?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit Connection">
                      <IconButton
                        size="small"
                        onClick={(e) => handleEditConnection(connection, e)}
                        sx={{ p: 0.5 }}
                        disabled={isDeleting}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isDeleting ? "Deleting..." : "Delete Connection"}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteConnection(connection.id, e)}
                        sx={{ 
                          p: 0.5,
                          color: isDeleting ? 'error.main' : 'inherit',
                        }}
                        disabled={isDeleting}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <ConnectionModal
        open={isConnectionModalOpen}
        onClose={() => {
          setIsConnectionModalOpen(false);
          setEditingConnection(null);
        }}
        editingConnection={editingConnection}
        selectedTargetStep={selectedTargetStep}
        setSelectedTargetStep={setSelectedTargetStep}
        triggerType={triggerType}
        setTriggerType={setTriggerType}
        delayDuration={delayDuration}
        setDelayDuration={setDelayDuration}
        delayUnit={delayUnit}
        setDelayUnit={setDelayUnit}
        funnelStep={funnelStep}
        setFunnelStep={setFunnelStep}
        eventType={eventType}
        setEventType={setEventType}
        conditionLabel={conditionLabel}
        setConditionLabel={setConditionLabel}
        conditionConfig={conditionConfig}
        setConditionConfig={setConditionConfig}
        priority={priority}
        setPriority={setPriority}
        availableSteps={availableSteps}
        onCreateConnection={handleCreateConnection}
        isValidConnection={isValidConnection}
        journey={journey}
        step={step}
      />
    </>
  );
};

export default StepNode;